import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Autocomplete,
  Grid,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { datePickerToCalendarValue } from '../../utils/dateUtils';
import DeductFromLocationSelect from '../Inventory/DeductFromLocationSelect';
import DeaBottleSelect from '../DEALogs/DeaBottleSelect';
import QuantityField from '../QuantityField';
import VaccinationLotPicker, { AUTO_LOT, LotSelection, lotPayload } from './VaccinationLotPicker';
import { vaccinationLotControls } from '../../auth/pageControls';
import { useRBAC } from '../../auth/useRBAC';
import { isSameDay } from 'date-fns';

interface CatalogItem {
  name: string;
  basePrice: number;
}

interface VaccinationRow {
  name: string;
  description: string;
  quantity: number;
  issuedBy: string;
  nextBooster: Date | null;
  // Stock location the dose deducts from — stamped from the dialog-level
  // "Deduct stock from" select; undefined = Automatic (server infers:
  // vet's truck if assigned, else warehouse — ADR-0013).
  dispenseLocationId?: string;
  // Bottle (ControlledSubstanceLot.id) for the DEA auto-log when the name
  // matches the clinic's CS registry; undefined = server FIFO pick (ADR-0014).
  deaLotId?: string;
  // mL given and the lot it came from (ADR-0047). With a lot, the mL is what leaves stock.
  doseMl: number | null;
  lot: LotSelection;
}

interface AutocompleteOption {
  name: string;
  price?: number;
  group: 'priced' | 'terminology';
}

interface VaccinationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (vaccinations: VaccinationRow[]) => Promise<void>;
  currentUserName: string;
  lowStockItems?: string[];
  showAll?: boolean;
  catalogItems?: CatalogItem[];
  /** Vaccines to start with, e.g. the ones a SOAP plan names. */
  initialNames?: string[];
  title?: string;
  /** A line above the form, e.g. why the dialog opened. */
  intro?: string;
  cancelLabel?: string;
  /** A second way out that carries the caller on (SOAP: complete without recording); Cancel stays an abort. */
  onSkip?: () => void;
  /** The day the form starts on (SOAP: the note's date). */
  initialDate?: Date;
}

const VaccinationDialog: React.FC<VaccinationDialogProps> = ({
  open, onClose, onSave, currentUserName, lowStockItems = [], showAll = true, catalogItems = [],
  initialNames, title = 'Add Vaccination', intro, cancelLabel = 'Cancel', onSkip, initialDate,
}) => {
  const [vaccinations, setVaccinations] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const rbac = useRBAC();
  const lotControls = vaccinationLotControls(rbac);

  const blankRow = (name = ''): VaccinationRow => ({
    name,
    description: '',
    quantity: 1,
    issuedBy: currentUserName,
    nextBooster: null,
    doseMl: 1,
    lot: AUTO_LOT,
  });

  const MAX_ROWS = 10;
  const [rows, setRows] = useState<VaccinationRow[]>([blankRow(), blankRow()]);
  // One job-level "Deduct stock from" for the whole save ('' = Automatic).
  const [dispenseLocationId, setDispenseLocationId] = useState<string>('');
  // Backdate freely: documented now, given earlier (spec §3).
  const [dateAdministered, setDateAdministered] = useState<Date | null>(new Date());
  // Rows whose lot is expired and not yet confirmed (or not this person's to confirm).
  // A backdated day, so the lot picker judges an expired lot by the day given, as the server will (ADR-0047 A5).
  const givenOn =
    dateAdministered && !isNaN(dateAdministered.getTime()) && !isSameDay(dateAdministered, new Date())
      ? datePickerToCalendarValue(dateAdministered)?.slice(0, 10)
      : undefined;
  const [unconfirmed, setUnconfirmed] = useState<Record<number, boolean>>({});

  // Seed the vaccines a caller already knows were given, and its day, once per opening.
  useEffect(() => {
    if (open && initialNames && initialNames.length > 0) {
      setRows(initialNames.slice(0, MAX_ROWS).map((n) => blankRow(n)));
    }
    if (open && initialDate && !isNaN(initialDate.getTime())) setDateAdministered(initialDate);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!showAll) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    fetch('/terminology/vaccinations.equine.json')
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then((data: string[]) => {
        setVaccinations(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setVaccinations([]);
        setLoading(false);
      });
  }, [open, showAll]);

  const handleRowChange = (index: number, field: keyof VaccinationRow, value: any) => {
    const updated = [...rows];
    // A lot belongs to one vaccine: renaming the row starts its lot over.
    updated[index] = { ...updated[index], [field]: value, ...(field === 'name' && value !== rows[index].name ? { lot: AUTO_LOT } : {}) };
    setRows(updated);
  };

  const handleSave = async () => {
    // A named row with an empty quantity box would be dropped by the filter below without a word.
    const missingQuantity = rows.find(r => r.name && !(r.quantity > 0));
    if (missingQuantity) {
      setError(`Enter a quantity for ${missingQuantity.name}.`);
      return;
    }
    const expired = rows.find((r, i) => r.name && unconfirmed[i]);
    if (expired) {
      setError(
        lotControls.recordExpiredLot
          ? `Confirm the expired lot for ${expired.name}, or choose another lot.`
          : `Choose another lot for ${expired.name}. Ask a veterinarian or admin to record a dose from an expired lot.`,
      );
      return;
    }
    if (!dateAdministered || isNaN(dateAdministered.getTime())) {
      setError('Enter the date the vaccines were given.');
      return;
    }
    // Today keeps the moment of saving, as before; a backdated day posts that calendar day.
    const givenAt = isSameDay(dateAdministered, new Date())
      ? new Date().toISOString()
      : datePickerToCalendarValue(dateAdministered);
    setSubmitting(true);
    setError(null);

    const filledRows = rows
      .filter(r => r.name && r.quantity > 0)
      // Stamp the job-level deduction location ('' = Automatic — omit so the
      // server infers, ADR-0013), and translate row fields to the API payload
      // names the save handlers read (nextDueDate/administeredBy/notes). The
      // row's nextBooster was previously dropped on save, so chart-recorded
      // vaccinations never carried a next-due date — and never generated
      // care reminders (ADR-0019).
      .map(r => ({
        ...r,
        dispenseLocationId: dispenseLocationId || undefined,
        // ── W10 REFERENCE CONVERSION (DATE-KINDS §2.5) ──────────────────────
        // `Vaccination.nextDueDate` is a CALENDAR day. Handing the raw picker
        // `Date` to axios serialises it with toISOString(), i.e. the BROWSER'S
        // midnight — a fourth anchor beside noon UTC, UTC midnight and iOS's
        // device-local midnight. It is benign for US clinics (T04:00Z/T05:00Z is
        // still the same UTC day) and rolls the day back AT STORAGE TIME for any
        // positive-offset user, where no render fix can recover it.
        // `datePickerToCalendarValue` reads the picker's LOCAL y/m/d — what the
        // user actually picked — and anchors it at noon UTC, the same value
        // barn ops now posts for this column (barnOperationsItems.ts).
        // ⚠ This is the pattern for every CALENDAR DatePicker. A DateTimePicker
        // collecting a real moment stays `date.toISOString()`.
        nextDueDate: datePickerToCalendarValue(r.nextBooster),
        administeredBy: r.issuedBy || undefined,
        notes: r.description || undefined,
        dateAdministered: givenAt,
        doseMl: r.doseMl && r.doseMl > 0 ? r.doseMl : null,
        ...lotPayload(r.lot),
      }));

    if (filledRows.length === 0) {
      setError('Please enter at least one vaccination');
      setSubmitting(false);
      return;
    }

    try {
      await onSave(filledRows);
      handleClose();
    } catch (err: any) {
      console.error('Failed to save vaccinations:', err);
      setError(err?.response?.data?.message || 'Failed to save vaccinations');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setRows([blankRow(), blankRow()]);
    setError(null);
    setDateAdministered(new Date());
    setUnconfirmed({});
  };

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  // Lots belong to a location: a new location starts every row's lot over.
  const handleLocationChange = (locationId: string) => {
    setDispenseLocationId(locationId);
    setRows(prev => prev.map(r => ({ ...r, lot: AUTO_LOT })));
    setUnconfirmed({});
  };

  const autocompleteOptions = useMemo((): AutocompleteOption[] => {
    const pricedOptions: AutocompleteOption[] = catalogItems
      .filter(item => item.name)
      .map(item => ({
        name: item.name,
        price: item.basePrice,
        group: 'priced' as const,
      }));

    if (!showAll) return pricedOptions;

    const pricedNamesLower = new Set(pricedOptions.map(o => o.name.toLowerCase()));

    const uniqueVaccineNames = Array.from(new Set(vaccinations))
      .sort((a, b) => a.localeCompare(b));

    const terminologyOptions: AutocompleteOption[] = uniqueVaccineNames
      .filter(name => !pricedNamesLower.has(name.toLowerCase()))
      .map(name => ({
        name,
        group: 'terminology' as const,
      }));

    return [...pricedOptions, ...terminologyOptions];
  }, [catalogItems, vaccinations, showAll]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {intro && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {intro}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {rows.some(r => r.name && lowStockItems.includes(r.name)) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <WarningIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Warning: Some selected vaccines are low in stock or need to be purchased.
          </Alert>
        )}

        {!loading && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 2 }}>
              {/* Location first (spec §11): lot suggestions below come from here. The select renders
                  only when the clinic has trucks (ADR-0013); otherwise every dose comes off the warehouse. */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Date given"
                    value={dateAdministered}
                    onChange={setDateAdministered}
                    disableFuture
                    slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DeductFromLocationSelect
                    value={dispenseLocationId}
                    onChange={handleLocationChange}
                    disabled={submitting}
                  />
                </Grid>
              </Grid>

              {rows.map((row, idx) => (
                <Box key={idx} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="subtitle2">Vaccination {idx + 1}</Typography>
                    {row.name && lowStockItems.includes(row.name) && (
                      <Chip
                        icon={<WarningIcon />}
                        label="Low Stock"
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={autocompleteOptions}
                        value={autocompleteOptions.find(o => o.name === row.name) || null}
                        onChange={(_, newValue) => {
                          const name = typeof newValue === 'string' ? newValue : newValue?.name || '';
                          handleRowChange(idx, 'name', name);
                        }}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.price != null
                            ? `${option.name} — $${option.price.toFixed(2)}`
                            : option.name;
                        }}
                        groupBy={showAll ? (option) => option.group === 'priced' ? 'Your Priced Items' : 'Medical Database' : undefined}
                        renderOption={(props, option) => (
                          <li {...props} key={`${option.name}-${option.group}`}>
                            <Box display="flex" flexDirection="column">
                              <Typography variant="body2" fontWeight={600}>
                                {option.name}
                                {option.price != null && (
                                  <Typography component="span" variant="body2" color="success.main" sx={{ ml: 1 }}>
                                    ${option.price.toFixed(2)}
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={params => (
                          <TextField {...params} label="Vaccine Name" size="small" required />
                        )}
                        filterOptions={(options, { inputValue }) => {
                          const lower = inputValue.toLowerCase();
                          return options.filter(o => o.name.toLowerCase().includes(lower));
                        }}
                        isOptionEqualToValue={(option, value) => option.name === value.name}
                        freeSolo
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            handleRowChange(idx, 'name', value);
                          }
                        }}
                        inputValue={row.name}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      {/* The dose billed and taken off stock, in hundredths (ADR-0038). */}
                      <QuantityField
                        label="Quantity"
                        size="small"
                        fullWidth
                        value={row.quantity}
                        onChange={next => handleRowChange(idx, 'quantity', next ?? 0)}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Issued By"
                        size="small"
                        fullWidth
                        value={row.issuedBy}
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        size="small"
                        fullWidth
                        value={row.description}
                        onChange={e => handleRowChange(idx, 'description', e.target.value)}
                        placeholder="e.g., Annual booster, rabies vaccine, etc."
                        multiline
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      {/* What went into the horse (spec §3, §10): with a lot, this is what leaves stock. */}
                      <QuantityField
                        label="Amount given (mL)"
                        size="small"
                        fullWidth
                        value={row.doseMl}
                        onChange={next => handleRowChange(idx, 'doseMl', next)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={9}>
                      <VaccinationLotPicker
                        vaccineName={row.name}
                        locationId={dispenseLocationId}
                        doseMl={row.doseMl}
                        value={row.lot}
                        onChange={next => setRows(prev => prev.map((r, i) => (i === idx ? { ...r, lot: next } : r)))}
                        onNeedsConfirmation={needs =>
                          setUnconfirmed(prev => (prev[idx] === needs ? prev : { ...prev, [idx]: needs }))
                        }
                        givenOn={givenOn}
                        disabled={submitting}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Next Booster Date"
                        value={row.nextBooster}
                        onChange={date => handleRowChange(idx, 'nextBooster', date)}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Grid>

                    {/* DEA bottle prompt — renders only when this row's name
                      * single-matches the clinic's CS registry (ADR-0014). */}
                    <Grid item xs={12}>
                      <DeaBottleSelect
                        drugName={row.name}
                        value={row.deaLotId ?? ''}
                        onChange={(lotId) => handleRowChange(idx, 'deaLotId', lotId || undefined)}
                        quantity={row.quantity || undefined}
                        disabled={submitting}
                        margin="none"
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}

              <Box textAlign="center">
                <Button
                  variant="outlined"
                  onClick={() => setRows(prev => (prev.length < MAX_ROWS ? [...prev, blankRow()] : prev))}
                  size="small"
                  disabled={rows.length >= MAX_ROWS}
                >
                  {rows.length >= MAX_ROWS ? 'Maximum Reached' : 'Add Vaccination'}
                </Button>
              </Box>

            </Box>
          </LocalizationProvider>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {cancelLabel}
        </Button>
        {onSkip && (
          <Button
            onClick={() => {
              reset();
              onSkip();
            }}
            disabled={submitting}
          >
            Skip and complete
          </Button>
        )}
        <Button onClick={handleSave} variant="contained" disabled={submitting || loading}>
          {submitting ? 'Saving...' : 'Save Vaccination'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VaccinationDialog;
